import { Bike, Package, Plus, ShoppingCart, Users } from "lucide-react";
import { Link } from "react-router";

import { InstallButton } from "@/components/shared/install-button";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useProducts } from "@/hooks/use-products";
import { useRiderSales } from "@/hooks/use-rider-sales";
import { useRiders } from "@/hooks/use-riders";
import { useSales } from "@/hooks/use-sales";

export function Dashboard() {
  const { data: salesData } = useSales({ per_page: 10 });
  const { data: riderSalesData } = useRiderSales({ per_page: 10 });
  const { data: productsData } = useProducts({ per_page: 100 });
  const { data: ridersData } = useRiders({ per_page: 100 });

  const totalSalesRevenue = (salesData?.items ?? []).reduce(
    (sum, s) => sum + (s.total ?? 0),
    0,
  );
  const totalRiderUnitsSold = (riderSalesData?.items ?? []).reduce(
    (sum, r) => sum + (r.totalSoldQty ?? 0),
    0,
  );
  const totalRiderRevenue = (riderSalesData?.items ?? []).reduce(
    (sum, r) => sum + (r.totalRevenue ?? 0),
    0,
  );

  const activeProductsCount = (productsData?.items ?? []).filter(
    (p) => p.isActive,
  ).length;
  const activeRidersCount = (ridersData?.items ?? []).filter(
    (r) => r.isActive,
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Ringkasan operasional dan penjualan JAM Coffee."
        action={
          <div className="flex items-center gap-2">
            <InstallButton />
            <Button asChild size="sm">
              <Link to="/sales">
                <Plus className="mr-1 h-4 w-4" />
                Catat Penjualan
              </Link>
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Penjualan Toko
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              Rp {totalSalesRevenue.toLocaleString("id-ID")}
            </div>
            <p className="text-xs text-muted-foreground">
              {salesData?.items?.length ?? 0} transaksi tercatat
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Penjualan Rider
            </CardTitle>
            <Bike className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalRiderUnitsSold.toLocaleString("id-ID")} unit
            </div>
            <p className="text-xs text-muted-foreground">
              Omzet: Rp {totalRiderRevenue.toLocaleString("id-ID")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Produk Aktif
            </CardTitle>
            <Package className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeProductsCount}</div>
            <p className="text-xs text-muted-foreground">
              Menu aktif siap jual
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Rider Aktif
            </CardTitle>
            <Users className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeRidersCount}</div>
            <p className="text-xs text-muted-foreground">
              Rider lapangan terdaftar
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sales & Rider Sales */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Penjualan Toko Terakhir */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">
              Penjualan Toko Terakhir
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/sales">Lihat Semua</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(salesData?.items ?? []).slice(0, 5).map((sale) => (
                <div
                  key={sale.id}
                  className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {sale.items[0]?.productName ?? "Penjualan"}
                      {sale.items.length > 1
                        ? ` +${sale.items.length - 1} lainnya`
                        : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {sale.date} &bull; {sale.channel}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">
                      Rp {(sale.total ?? 0).toLocaleString("id-ID")}
                    </p>
                    <Badge variant="outline" className="text-[10px]">
                      {sale.paymentStatus}
                    </Badge>
                  </div>
                </div>
              ))}
              {(salesData?.items ?? []).length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  Belum ada transaksi penjualan.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Penjualan Rider Terakhir */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">
              Penjualan Rider Terakhir
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/rider-sales">Lihat Semua</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(riderSalesData?.items ?? []).slice(0, 5).map((rs) => (
                <div
                  key={rs.id}
                  className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="text-sm font-medium">{rs.riderName}</p>
                    <p className="text-xs text-muted-foreground">
                      {rs.date} &bull; Terjual: {rs.totalSoldQty} / Ambil:{" "}
                      {rs.totalDispatchedQty}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">
                      Rp {(rs.totalRevenue ?? 0).toLocaleString("id-ID")}
                    </p>
                    <span className="text-[11px] text-muted-foreground">
                      Bonus: Rp {(rs.totalBonus ?? 0).toLocaleString("id-ID")}
                    </span>
                  </div>
                </div>
              ))}
              {(riderSalesData?.items ?? []).length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  Belum ada data penjualan rider.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
